import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Menu } from "antd";
import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  UserOutlined,
  LineChartOutlined,
  BarChartOutlined,
  AliwangwangOutlined,
  DeploymentUnitOutlined,
  UsergroupAddOutlined,
  ApartmentOutlined,
  FileTextOutlined,
  ProfileFilled,
  SendOutlined,
  TeamOutlined,
  SoundOutlined,
  SafetyCertificateOutlined,
  PieChartOutlined,
  ContactsOutlined,
  AppstoreOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Sider from "antd/es/layout/Sider";
import "./sidebar.scss";
import { PAGE_ROUTES, Roles } from "../../utils/constants";
import AppSecureStorage from "../../services/secureStorage";
import { useDispatch } from "react-redux";
import {
  isAnalyticsGet,
  isChatGet,
  isCustomActionsGet,
  isIntegrationGet,
  isOrganization,
  isProfileGet,
  isTrainAndContentGet,
  isUserManagementGet,
  isUserRoleGet,
  isBroadcastAdd,
  isBroadcastGet,
  isBroadcastEdit,
  isBroadcastDelete,
} from "../../utils/permissions";
import { setPage, setSearchInput } from "../../redux/slices/globalSlice";

const storage = new AppSecureStorage();

const Sidebar = () => {
  const dispatch = useDispatch();
  const [collapsed, setCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  const toggle = useCallback(() => {
    setCollapsed((prevCollapsed) => !prevCollapsed);
  }, []);

  const handleLogout = () => {
    storage.clearAuthData();
    navigate("/");
  };
  const billingChildren = [
    {
      key: "5",
      icon: <AppstoreOutlined />,
      label: "Overview",
      path: PAGE_ROUTES.BILLING,
    },
    // {
    //   key: "11",
    //   icon: <FileTextOutlined />,
    //   label: "Invoices",
    //   path: PAGE_ROUTES.BILLING_INVOICE,
    // },
    isOrganization === "superadmin" && {
      key: "12",
      icon: <FileTextOutlined />,
      label: "Invoice Approvals",
      path: PAGE_ROUTES.BILLING_INVOICE_APPROVAL,
    },
  ].filter(Boolean);

  const menuItems = useMemo(() => {
    return [
      isChatGet && {
        key: "1",
        icon: <AliwangwangOutlined />,
        label: "Chat",
        path: PAGE_ROUTES.CHAT,
      },
      isIntegrationGet && {
        key: "2",
        icon: <DeploymentUnitOutlined />,
        label: "Integration",
        path: PAGE_ROUTES.INTEGRATION,
      },
      isProfileGet && {
        key: "3",
        icon: <UserOutlined />,
        label: "Profile",
        path: PAGE_ROUTES.PROFILE,
      },
      isAnalyticsGet && {
        key: "4",
        icon: <LineChartOutlined />,
        label: "Analytics",
        path: PAGE_ROUTES.ANALYTICS,
      },
      // isAnalyticsGet &&
      //   billingChildren.length > 0 && {
      //     key: "sub2",
      //     icon: <BarChartOutlined />,
      //     label: "Billing",
      //     children: billingChildren,
      //   },
      isUserRoleGet && {
        key: "6",
        icon: <SafetyCertificateOutlined />,
        label: "User Roles",
        path: PAGE_ROUTES.USER_ROLES,
      },
      isUserManagementGet && {
        key: "7",
        icon: <TeamOutlined />,
        label: "User Management",
        path: PAGE_ROUTES.USER_MANAGEMENT,
      },
      isOrganization === Roles.SUPER_ADMIN && {
        key: "8",
        icon: <ApartmentOutlined />,
        label: "Organization",
        path: PAGE_ROUTES.ORGANIZATION,
      },
      (isTrainAndContentGet || isCustomActionsGet) && {
        key: "sub1",
        icon: <FileTextOutlined />,
        label: "Train and Content",
        children: [
          isTrainAndContentGet && {
            key: "9",
            icon: <ProfileFilled />,
            label: "Content",
            path: PAGE_ROUTES.CONTENT,
          },
          // isCustomActionsGet && {
          //   key: "10",
          //   icon: <UsergroupAddOutlined />,
          //   label: "Custom",
          //   path: PAGE_ROUTES.CUSTOM,
          // },
        ].filter(Boolean),
      },
      // Broadcast menu items based on role
      (isBroadcastAdd ||
        isBroadcastGet ||
        isBroadcastEdit ||
        isBroadcastDelete) && {
        key: "sub3",
        icon: <SoundOutlined />,
        label: "Broadcast",
        children: [
          isBroadcastAdd && {
            key: "broadcast-send",
            icon: <SendOutlined />,
            label: "Send Message",
            path: PAGE_ROUTES.BROADCAST_SEND_MESSAGE,
          },
          isBroadcastGet && {
            key: "broadcast-scheduled",
            icon: <ClockCircleOutlined />,
            label: "Scheduled",
            path: PAGE_ROUTES.BROADCAST_SCHEDULED,
          },
          isBroadcastGet && {
            key: "broadcast-analytics",
            icon: <PieChartOutlined />,
            label: "Analytics",
            path: PAGE_ROUTES.BROADCAST_ANALYTICS,
          },
          (isBroadcastAdd || isBroadcastGet) && {
            key: "broadcast-template",
            icon: <FileTextOutlined />,
            label: "Template",
            path: PAGE_ROUTES.BROADCAST_TEMPLATE,
          },
          (isBroadcastEdit || isBroadcastDelete) && {
            key: "broadcast-user-groups",
            icon: <ContactsOutlined />,
            label: "User Groups",
            path: PAGE_ROUTES.BROADCAST_USER_GROUPS,
          },
        ].filter(Boolean),
      },
    ].filter(Boolean);
  }, []);

  const renderMenuItems = (items) => {
    return items.map((item) => {
      if (item.children) {
        return (
          <Menu.SubMenu key={item.key} icon={item.icon} title={item.label}>
            {renderMenuItems(item.children)}
          </Menu.SubMenu>
        );
      }
      return (
        <Menu.Item
          onClick={() => {
            dispatch(setSearchInput(""));
            navigate(item.path);
          }}
          key={item.key}
          icon={item.icon}
        >
          <Link onClick={() => navigate(item.path)} to={item.path}>
            {item.label}
          </Link>
        </Menu.Item>
      );
    });
  };

  const getSelectedKey = () => {
    const findActiveKey = (items) => {
      for (let item of items) {
        if (item.path === location.pathname) {
          return item.key;
        }
        if (item.children) {
          const childKey = findActiveKey(item.children);
          if (childKey) return childKey;
        }
      }
      return null;
    };
    return findActiveKey(menuItems) || "1";
  };

  const handleOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  useEffect(() => {
    if (location.pathname !== PAGE_ROUTES.CHAT) {
      dispatch(setPage(1));
    }

    const isInTrainAndContent =
      location.pathname.startsWith(PAGE_ROUTES.CONTENT) ||
      location.pathname.startsWith(PAGE_ROUTES.CUSTOM);
    const isInBilling =
      location.pathname.startsWith(PAGE_ROUTES.BILLING) ||
      location.pathname.startsWith(PAGE_ROUTES.BILLING_INVOICE);
    const isInBroadcast =
      location.pathname.startsWith(PAGE_ROUTES.BROADCAST_SEND_MESSAGE) ||
      location.pathname.startsWith(PAGE_ROUTES.BROADCAST_SCHEDULED) ||
      location.pathname.startsWith(PAGE_ROUTES.BROADCAST_ANALYTICS) ||
      location.pathname.startsWith(PAGE_ROUTES.BROADCAST_USER_GROUPS) ||
      location.pathname.startsWith(PAGE_ROUTES.BROADCAST_TEMPLATE);

    if (!isInTrainAndContent && !isInBilling && !isInBroadcast) {
      setOpenKeys([]);
    } else if (isInBilling) {
      setOpenKeys(["sub2"]);
    } else if (isInBroadcast) {
      setOpenKeys(["sub3"]);
    } else if (isInTrainAndContent) {
      setOpenKeys(["sub1"]);
    }
  }, [location, dispatch]);

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      className="sider"
      breakpoint="md"
      width={210}
      collapsedWidth={80}
      onBreakpoint={(broken) => broken && setCollapsed(true)}
    >
      <div className="logo" />
      {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
        className: `trigger ${collapsed ? "collapsed" : "expanded"}`,
        onClick: toggle,
      })}
      <div className="menu-wrapper">
        <Menu
          mode="inline"
          selectedKeys={[getSelectedKey()]}
          openKeys={openKeys}
          onOpenChange={handleOpenChange}
          className="menu-container"
        >
          {renderMenuItems(menuItems)}
        </Menu>
      </div>
      <div className="logout-menu">
        <Menu mode="inline">
          <Menu.Item key="a0" icon={<UserOutlined />} onClick={handleLogout}>
            Logout
          </Menu.Item>
        </Menu>
      </div>
    </Sider>
  );
};

export default Sidebar;
